<template>
  <div class="container">
    <h5 class="mb-1 title-text">
      {{ t("PAYJOY_SHIPMENT_DETAILS.SHIPPED_CARD_DETAILS_TITLE") }}
    </h5>
    <div class="shipment-details">
      <ShipmentDetails
        :estimatedDeliveryDate="estimatedDeliveryDate"
        :address="address"
        :provider="provider"
        :orderNumber="orderNumber"
      />
      <div>
        <TrackingStatus :shipmentLogs="statuses" />
      </div>
    </div>
    <div class="floating-btn">
      <template v-if="isFailedStatus">
        <WhatsAppButton @click="openWhatsApp" />
      </template>
      <template v-else>
        <div class="floating-btn-content">
          <Button id="btn-continue" @click="goToActivation">
            <div class="text-white text-center my-auto fw-bold btn-text">
              <span>{{
                t("PAYJOY_SHIPMENT_DETAILS.SHIPPED_CARD_ACTIVATE_BUTTON")
              }}</span>
            </div>
          </Button>
          <p
            v-if="
              isDeliveredStatus &&
              props.payjoyCardStatus !== PayJoyCardStatus.REPORTED_UNDELIVERED
            "
            class="card-not-received mt-2 mb-0"
            @click="reportNotDelivered"
          >
            {{ t("PAYJOY_SHIPMENT_DETAILS.SHIPPED_CARD_NOT_RECEIVED_BUTTON") }}
          </p>
        </div>
      </template>
    </div>

    <DownUpModal
      v-if="showPhysicalCardActivationNotAllowedModal"
      :show-actions="false"
      :modal-header-image="redWarningIcon"
      :modal-header-title="t('VIRTUAL_CARD_CREATION_NOT_ALLOWED_MODAL.TITLE')"
      :modal-header-subtitle="
        t('VIRTUAL_CARD_CREATION_NOT_ALLOWED_MODAL.DESCRIPTION')
      "
      @click-overlay="showPhysicalCardActivationNotAllowedModal = false"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { AndroidInterface } from "@/apps/credit-line/services/android.service";
import { Service } from "@/apps/credit-line/services/network.service";
import { AuthenticationHelper } from "@/apps/credit-line/helpers/authentication.helper";
import TrackingStatus from "./TrackingStatus.vue";
import Button from "@/apps/credit-line/components/Button.vue";
import { useRouter } from "vue-router";
import ShipmentDetails from "./ShipmentDetails.vue";
import WhatsAppButton from "@/apps/credit-line/components/WhatsAppButton.vue";
import {
  getSetting,
  getSettings,
} from "@/apps/credit-line/helpers/consumerSettings.helper";
import { useI18n } from "@/apps/credit-line/composables/useI18n";
import { PayJoyCardStatus } from "@/apps/credit-line/models/PayJoyCardStatus";
import { SettingsCategory } from "@/shared-models/consumer/SettingsCategory";
import DownUpModal from "@/apps/credit-line/components/DownUpModal.vue";
import useAsset from "@/apps/credit-line/helpers/useAsset";

declare const Android: AndroidInterface;
const { t } = useI18n();
const orderNumber = ref<string>("");
const address = ref<string>("");
const provider = ref<string>("");
const CreditLineService = new Service();
const estimatedDeliveryDate = ref<string>("");
const deviceTag = ref<string>("");
const redWarningIcon = useAsset("img/credit-line/red_warning_icon.png");
const whatsAppSupportNumber = ref<string>("");
const allowPhysicalCardActivation = ref<boolean>(false);
const showPhysicalCardActivationNotAllowedModal = ref<boolean>(false);
const statuses = ref<Array<{ id: number; title: string; description: string }>>(
  []
);
const router = useRouter();

const props = defineProps({
  creditLineId: {
    type: Number,
    required: true,
  },
  payjoyCardStatus: {
    type: String as () => PayJoyCardStatus,
    required: true,
  },
});

const emit = defineEmits<{
  (e: "reportNotDelivered", value: boolean): void;
}>();

const reportNotDelivered = () => {
  emit("reportNotDelivered", true);
};

const openWhatsApp = () => {
  const helpMsg = t("CL_WP_SHIPMENTS_HELP_MSG", {
    deviceTag: deviceTag.value,
  });
  const wpNumber = whatsAppSupportNumber.value;
  window.open(`https://wa.me/${wpNumber}?text=` + helpMsg, "_system");
};

const initializeSettings = async () => {
  try {
    const helpSettings = await getSettings(SettingsCategory.CL_HELP_PAGE);
    const allowPhysicalCardActivationSetting = await getSetting(
      "allowPhysicalCardActivation",
      SettingsCategory.CL_CARDS_PAGE
    );
    allowPhysicalCardActivation.value =
      allowPhysicalCardActivationSetting.enabled;
    whatsAppSupportNumber.value =
      helpSettings.whatsAppSupportNumber.values?.phone;
  } catch (error) {
    console.error("Error initializing settings:", error);
  }
};

const goToActivation = () => {
  if (allowPhysicalCardActivation.value) {
    return void router.push({ name: "CreditCardActivationView" });
  }

  showPhysicalCardActivationNotAllowedModal.value = true;
};

const hideNav = () => {
  if (Android && typeof Android?.hideNav === "function") {
    Android.hideNav();
  }
};

const statusMapper: Record<string, { title: string; description: string }> = {
  CREATED: {
    title: t("PAYJOY_SHIPMENT_DETAILS.SHIPPED_CARD_CREATED_STATUS_TITLE"),
    description: t(
      "PAYJOY_SHIPMENT_DETAILS.SHIPPED_CARD_CREATED_STATUS_DESCRIPTION"
    ),
  },
  IN_TRANSIT: {
    title: t("PAYJOY_SHIPMENT_DETAILS.SHIPPED_CARD_IN_TRANSIT_STATUS_TITLE"),
    description: t(
      "PAYJOY_SHIPMENT_DETAILS.SHIPPED_CARD_IN_TRANSIT_STATUS_DESCRIPTION"
    ),
  },
  IN_WAREHOUSE: {
    title: t("PAYJOY_SHIPMENT_DETAILS.SHIPPED_CARD_IN_WAREHOUSE_STATUS_TITLE"),
    description: t(
      "PAYJOY_SHIPMENT_DETAILS.SHIPPED_CARD_IN_WAREHOUSE_STATUS_DESCRIPTION"
    ),
  },
  DELIVERED: {
    title: t("PAYJOY_SHIPMENT_DETAILS.SHIPPED_CARD_DELIVERED_STATUS_TITLE"),
    description: t(
      "PAYJOY_SHIPMENT_DETAILS.SHIPPED_CARD_DELIVERED_STATUS_DESCRIPTION"
    ),
  },
  TRACKED: {
    title: t("PAYJOY_SHIPMENT_DETAILS.SHIPPED_CARD_TRACKED_STATUS_TITLE"),
    description: t(
      "PAYJOY_SHIPMENT_DETAILS.SHIPPED_CARD_TRACKED_STATUS_DESCRIPTION"
    ),
  },
  FAILED_DELIVERY_ATTEMPT: {
    title: t(
      "PAYJOY_SHIPMENT_DETAILS.SHIPPED_CARD_FAILED_DELIVERY_ATTEMPT_STATUS_TITLE"
    ),
    description: t(
      "PAYJOY_SHIPMENT_DETAILS.SHIPPED_CARD_FAILED_DELIVERY_ATTEMPT_STATUS_DESCRIPTION"
    ),
  },
};

const failedStates = [
  "DELIVERY_ERROR",
  "NOT_DELIVERED",
  "REJECTED",
  "CANCELED",
];
failedStates.forEach((state) => {
  statusMapper[state] = {
    title: t("PAYJOY_SHIPMENT_DETAILS.SHIPPED_CARD_ERROR_STATUS_TITLE"),
    description: t(
      "PAYJOY_SHIPMENT_DETAILS.SHIPPED_CARD_ERROR_STATUS_DESCRIPTION"
    ),
  };
});

function mapShipmentStatuses(
  // eslint-disable-next-line  @typescript-eslint/no-explicit-any
  rawStatuses: Array<{ status: string; timestamp?: any }>
): Array<{ id: number; title: string; description: string }> {
  return rawStatuses.map((statusObj, idx) => {
    const mapping = statusMapper[statusObj.status] || {
      title: statusObj.status,
      description: "",
    };
    return {
      id: idx + 1,
      title: mapping.title,
      description: mapping.description,
    };
  });
}

async function fetchStatuses() {
  try {
    const response = await CreditLineService.getShipmentLogs({
      deviceTag: deviceTag.value,
      creditLineId: props.creditLineId,
    });
    const rawStatuses = Array.isArray(response.data.shipmentStatuses)
      ? response.data.shipmentStatuses
      : [];
    statuses.value = mapShipmentStatuses(rawStatuses);
  } catch (error) {
    console.error("Error fetching statuses:", error);
  }
}

const fetchLastShipment = async () => {
  try {
    const response = await CreditLineService.getLatestShipment({
      deviceTag: deviceTag.value,
      creditLineId: props.creditLineId,
    });

    const latestShipment = response.data;
    const city = latestShipment?.address?.city;
    const region = latestShipment?.address?.region;
    const streetName = latestShipment?.address?.street_name;
    const additionalInfo = latestShipment?.address?.additional_info;
    const zipCode = latestShipment?.address?.zip_code;
    const streetNumber = latestShipment?.address?.street_number;

    if (latestShipment.address == null) {
      address.value = "";
    } else {
      address.value = `${streetName} ${streetNumber}, ${city}, ${region}, CP: ${zipCode}, ${additionalInfo}`;
    }
    provider.value = response.data.vendor;
    estimatedDeliveryDate.value = response.data.estimatedDeliveryDate;
    orderNumber.value = response.data.trackingNumber;
  } catch (error) {
    console.error("Error fetching last shipment:", error);
  }
};

const isFailedStatus = computed(() => {
  if (!statuses.value.length) return false;
  const latest = statuses.value[0];
  return (
    latest.title ===
    t("PAYJOY_SHIPMENT_DETAILS.SHIPPED_CARD_ERROR_STATUS_TITLE")
  );
});

const isDeliveredStatus = computed(() => {
  if (!statuses.value.length) return false;
  const latest = statuses.value[0];
  return (
    latest.title ===
    t("PAYJOY_SHIPMENT_DETAILS.SHIPPED_CARD_DELIVERED_STATUS_TITLE")
  );
});

onMounted(async () => {
  hideNav();
  deviceTag.value = AuthenticationHelper?.getDeviceTag() || "";
  await fetchLastShipment();
  await fetchStatuses();
  await initializeSettings();
});
</script>

<style scoped lang="scss">
@import "@/assets/scss/_constants.scss";

:root {
  --shipment-key-width: 120px;
}

.title-text {
  font-weight: 600;
  font-size: 20px;
  line-height: 29px;
  font-family: "Inter", sans-serif;
  color: #333333;
}

.floating-btn {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100vw;
  background: #fff;
  color: #fff;
  font-size: 18px;
  font-weight: 600;
  border: none;
  border-radius: 0;
  z-index: 100;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.04);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 16px;
}

.floating-btn-content {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.card-not-received {
  font-family: Inter;
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: 24px;
  color: #007c3b;
  text-align: center;
  margin-top: 8px;
  margin-bottom: 0;
  white-space: normal;
  word-break: break-word;
  max-width: 90vw;
}
</style>
